import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

export const CategoriesPanel = () => {
    const [cats, setCats] = useState([]);
    const [editing, setEditing] = useState(null);
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");

    const load = useCallback(() => {
        api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (e) => {
        e.preventDefault();
        try {
            if (editing) {
                await api.put(`/categories/${editing}`, { name_ar: nameAr, name_en: nameEn });
                toast.success("تم تحديث التصنيف");
            } else {
                await api.post("/categories", { name_ar: nameAr, name_en: nameEn });
                toast.success("تمت إضافة التصنيف");
            }
            setEditing(null);
            setNameAr("");
            setNameEn("");
            load();
        } catch {
            toast.error("تعذر الحفظ");
        }
    };

    const remove = async (c) => {
        if (!window.confirm(`حذف تصنيف «${c.name_ar}»؟`)) return;
        try {
            await api.delete(`/categories/${c.id}`);
            toast.success("تم الحذف");
            load();
        } catch {
            toast.error("تعذر الحذف");
        }
    };

    const inputCls =
        "bg-[#0A1228]/70 border border-white/10 rounded-xl py-2.5 px-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors duration-300";

    return (
        <div data-testid="admin-categories-panel" className="max-w-2xl">
            <h2 className="font-display font-bold text-xl text-white mb-6">التصنيفات</h2>

            <form onSubmit={save} className="spotlight-card border border-white/10 rounded-2xl p-5 mb-6 flex flex-wrap gap-3 items-end">
                <input required value={nameAr} onChange={(e) => setNameAr(e.target.value)} placeholder="اسم التصنيف (عربي)" data-testid="cat-name-ar" className={`${inputCls} flex-1 min-w-[140px]`} />
                <input value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="Name (EN)" data-testid="cat-name-en" className={`${inputCls} flex-1 min-w-[140px]`} dir="ltr" />
                <button type="submit" data-testid="cat-save-button" className="flex items-center gap-2 bg-amber-400 text-[#0A1228] font-extrabold text-sm rounded-xl px-5 py-2.5 hover:bg-amber-300 transition-colors duration-300">
                    {editing ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    {editing ? "حفظ" : "إضافة"}
                </button>
                {editing && (
                    <button type="button" onClick={() => { setEditing(null); setNameAr(""); setNameEn(""); }} className="p-2.5 rounded-xl border border-white/15 text-slate-400 hover:text-white" aria-label="إلغاء">
                        <X className="w-4 h-4" />
                    </button>
                )}
            </form>

            <div className="space-y-2">
                {cats.length === 0 && <p className="text-slate-500 text-sm">لا توجد تصنيفات بعد</p>}
                {cats.map((c) => (
                    <div key={c.id} className="flex items-center justify-between border border-white/10 rounded-2xl px-5 py-3.5 bg-[#101B3D]/60" data-testid={`cat-row-${c.id}`}>
                        <div>
                            <span className="font-bold text-white text-sm">{c.name_ar}</span>
                            {c.name_en && <span className="text-xs text-slate-500 mr-2" dir="ltr">{c.name_en}</span>}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => { setEditing(c.id); setNameAr(c.name_ar); setNameEn(c.name_en || ""); }}
                                data-testid={`cat-edit-${c.id}`}
                                className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-amber-400 hover:border-amber-400/50 transition-colors duration-200"
                                aria-label="تعديل"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => remove(c)}
                                data-testid={`cat-delete-${c.id}`}
                                className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-500/50 transition-colors duration-200"
                                aria-label="حذف"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
